import { gql } from '@apollo/client';
import { graphql } from '@xolv-mybrightlink/common';
import type { PssSurvey } from '../models/PssSurvey';
import { parseISO, isAfter } from 'date-fns';
import type { PssSurveyResponse } from '../models/PssSurveyResponse';

const GET_PSS_SURVEY = gql`
  query pssSurveys($allSurvey: Boolean) {
    pssSurveys(allSurvey: $allSurvey) {
      distributionId
      surveyLink
      patientSfId
      patientFirstName
      patientLastName
      surveyCompleted
      surveyCompletedAt
      expirationDate
      sentAt
      status
    }
  }
`;

const MARK_PSS_SURVEY_COMPLETED = gql`
  mutation markCarePathSurveyAsCompleted($patientSfId: String) {
    markDistributionAsComplete(patientSfId: $patientSfId)
  }
`;

const DISTRIBUTION_STATUS = gql`
  query distributionStatus($distributionId: String) {
    distributionStatus(distributionId: $distributionId)
  }
`;

const pssSurveyFromJson = (json: PssSurveyResponse): PssSurvey => {
  return {
    type: 'PSS',
    status: json.status,
    distributionId: json.distributionId,
    patientFirstName: json.patientFirstName,
    patientLastName: json.patientFirstName,
    patientSfId: json.patientSfId,
    surveyLink: json.surveyLink,
    expirationDate: parseISO(json.expirationDate),
    surveyCompletedAt: json.surveyCompletedAt,
  };
};

export const pssSurveys = async (allSurvey = true): Promise<PssSurvey[]> => {
  try {
    const response = await graphql.query<{
      pssSurveys: PssSurveyResponse[];
    }>({
      query: GET_PSS_SURVEY,
      variables: { allSurvey },
      fetchPolicy: 'no-cache',
    });

    return response.data?.pssSurveys
      .map((survey: PssSurveyResponse) => pssSurveyFromJson(survey))
      .filter((survey: PssSurvey) => {
        if (survey.status == 'SurveyFinished') return false;
        return (
          survey.expirationDate && isAfter(survey.expirationDate, new Date())
        );
      });
  } catch (e) {
    console.debug('Failed to get pss surveys');
    return [];
  }
};

export const markPssSurveyComplete = async (patientSfId: string) => {
  try {
    await graphql.mutate({
      mutation: MARK_PSS_SURVEY_COMPLETED,
      variables: { patientSfId },
    });
  } catch (e) {
    console.debug(`Failed to mark pss survey for ${patientSfId} as complete`);
  }
};

export const distributionStatus = async (
  distributionId: string,
): Promise<boolean> => {
  try {
    const response = await graphql.query<{
      distributionStatus: boolean;
    }>({
      query: DISTRIBUTION_STATUS,
      variables: { distributionId },
      fetchPolicy: 'no-cache',
    });

    return response.data?.distributionStatus ?? false;
  } catch (e) {
    console.debug(`Failed to ger distribution status for ${distributionId}`);
    return false;
  }
};
